import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { BookingType } from '@/types';

interface SavedBookingType {
  id: string;
  name: string;
  code: string;
  config: BookingType;
  savedAt: string;
}

interface SavedBookingTypesContextType {
  savedBookingTypes: SavedBookingType[];
  saveBookingType: (bookingType: BookingType) => void;
  deleteSavedBookingType: (id: string) => void;
  getSavedBookingType: (id: string) => SavedBookingType | undefined;
}

const SavedBookingTypesContext = createContext<SavedBookingTypesContextType | undefined>(undefined);

const STORAGE_KEY = 'saved_booking_types';

export function SavedBookingTypesProvider({ children }: { children: ReactNode }) {
  const [savedBookingTypes, setSavedBookingTypes] = useState<SavedBookingType[]>(() => {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Error loading saved booking types:', error);
      return [];
    }
  });

  // Persist to session storage whenever savedBookingTypes changes
  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(savedBookingTypes));
    } catch (error) {
      console.error('Error saving booking types to session storage:', error);
    }
  }, [savedBookingTypes]);

  const saveBookingType = (bookingType: BookingType) => {
    const savedType: SavedBookingType = {
      id: `saved-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      name: bookingType.name,
      code: bookingType.code,
      config: bookingType,
      savedAt: new Date().toISOString(),
    };

    setSavedBookingTypes(prev => {
      // Check if a booking type with the same name already exists
      const existingIndex = prev.findIndex(bt => bt.name === bookingType.name);
      if (existingIndex >= 0) {
        // Update existing
        const updated = [...prev];
        updated[existingIndex] = savedType;
        return updated;
      }
      // Add new
      return [...prev, savedType];
    });
  };

  const deleteSavedBookingType = (id: string) => {
    setSavedBookingTypes(prev => prev.filter(bt => String(bt.id) !== String(id)));
  };

  const getSavedBookingType = (id: string) => {
    return savedBookingTypes.find(bt => String(bt.id) === String(id));
  };

  return (
    <SavedBookingTypesContext.Provider
      value={{
        savedBookingTypes,
        saveBookingType,
        deleteSavedBookingType,
        getSavedBookingType,
      }}
    >
      {children}
    </SavedBookingTypesContext.Provider>
  );
}

export function useSavedBookingTypes() {
  const context = useContext(SavedBookingTypesContext);
  if (context === undefined) {
    throw new Error('useSavedBookingTypes must be used within a SavedBookingTypesProvider');
  }
  return context;
}
